import { Box, Button, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import SearchBar from "../common-components/Search-bar";
import { Add, CheckBoxOutlineBlankRounded, CheckBoxRounded } from "@mui/icons-material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useTranslation } from "react-i18next";
import EmployeeOnboardingStepper from "./EmployeeOnboardingStepper";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import EmployeeTableColumns from "./EmployeeTableColumns";
import ViewEmployee from "./ViewEmployee";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { formatDateDDMMMYYYY } from "../reports/ColumnUtils";
import { errorToster, warnToster } from "../../shared/toster/Toster";
import { Helper } from "../../utils/Helpers";
const { getGradientTextStyle } = new Helper();

const commonIconButtonStyles = {
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  width: { sm: "4.317vw", md: "2.778vw" },
  height: "85%",
  border: "1px solid #F7A86E",
  color: "#F7A86E",
};

const EmployeeDetails = ({ toggle }) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [formOpenState, setFormOpenState] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const { api } = ApiClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [tableData, setTableData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const isLargeScreen = useMediaQuery("(min-width:900px)");
  
  const handleEditEmployee = (employeeData) => {
    setEditData(employeeData);
    setFormOpenState(true);
  };

  const handleAddEmployee = () => {
    setEditData(null);
    setFormOpenState(true);
  };

  const handleViewEmployee = (employeeData) => {
    setViewData(employeeData?.data?.[0] ?? null);
    setViewEmployee(true);
  };

  const handleCloseView = () => {
    setViewEmployee(false);
    setViewData(null);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [debouncedSearchQuery]);

  useEffect(() => {
    getTableData(false);
  }, [
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    debouncedSearchQuery,
  ]);

  const getTableData = async (isPrint) => {
    let formattedData = [];
    if (!tableData.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await api.get(
        END_POINT.EMPLOYEE.GET_ALL_EMPLOYEES,
        {
          page_size: isPrint ? rowCount : pagination.pageSize,
          offset: isPrint ? 0 : pagination.pageIndex * pagination.pageSize,
          custom_filters: JSON.stringify(columnFilters ?? []),
          global_filter: globalFilter ?? "",
          sorting: JSON.stringify(sorting ?? []),
          search_key: debouncedSearchQuery,
          employee_key: ""
        }
      );
      if (response.status === 200) {
        const { data, count } = response?.data ?? {};
        formattedData = data?.map((item, index) => ({
          ...item,
          serial_no: index + 1 + pagination.pageIndex * pagination.pageSize,
        }));
        if (isPrint) {
          return formattedData;
        } else {
          setTableData(formattedData);
          setRowCount(count || 0);
        }
      }
    } catch (error) {
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
      setIsError(false);
      setIsRefetching(false);
    }
  };

  
  const handleRefresh = async () => {
    try {
      await getTableData(false);
      setSelectedRows({});
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const columns = useMemo(() => 
    EmployeeTableColumns(t, handleEditEmployee, handleRefresh, handleViewEmployee), 
    [t] 
  );

  
  const handleFormClose = async (shouldRefresh = false) => {
    setFormOpenState(false);
    setEditData(null);
    
  
    if (shouldRefresh) {
      await handleRefresh();
    }
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const buttonConfigs = [
    {
      title: "Download",
      onClick: () => handlePrintOrDownload("download"),
      icon: (
        <FileDownloadOutlinedIcon
          sx={{ fontSize: { sm: "2.59vw", md: "1.667vw" } }}
        />
      ),
    },
  ];

  const handleExportAllData = async (exportColumns) => {
    try {
      const data = await getTableData(true);
      await exportData(data, exportColumns);
    } catch (error) {
      errorToster("Something went wrong!");
    }
  };

  const exportData = async (data, columns) => {
    if (!data || data.length === 0) {
      warnToster(t("pages.reports.no_data_to_export"));
      return;
    }
    const formattedData = data.map((row, index) => {
      const rowData = {};
      columns.forEach((column) => {
        const key = column.accessorKey || column.id;
        const header = column.header;
        let value = "";
        switch (key) {
          case "serial_no":
            value = index + 1;
            break;
          case "name":
          case "mobile":
          case "alternate_mobile":
          case "gender":
          case "email":
          case "mail":
            value = row[key] ?? "-";
            break;
          case "dob":
            value = row[key] ? formatDateDDMMMYYYY(row[key]) : "-";
            break;
          default:
            value = row[key] ?? "";
            break;
        }
        if (typeof value === "object" && value !== null) {
          value = JSON.stringify(value);
        }
        rowData[header] = value;
      });
      return rowData;
    });

    const csvConfig = mkConfig({
      fieldSeparator: ",",
      decimalSeparator: ".",
      useKeysAsHeaders: true,
    });
    const csvContent = generateCsv(csvConfig)(formattedData);
    download(csvConfig)(csvContent);
    setSelectedRows({});
  };

  const handlePrintOrDownload = async () => {
    const visibleColumns = table
      .getAllLeafColumns()
      .filter(
        (col) =>
          col.getIsVisible() &&
          col.id !== "Select" &&
          col.id !== "mrt-row-select"
      );
    const exportColumns = visibleColumns.map((col) => ({
      header: col.columnDef.header,
      accessorKey: col.columnDef.accessorKey || col.id,
    }));

    if (Object.keys(selectedRows)?.length === 0) {
      handleExportAllData(exportColumns);
    } else {
      const unique_id = Object.keys(selectedRows);
      const data = await getTableData(true, unique_id);
      await exportData(data, exportColumns);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    getRowId: (row) => row?.employee_id,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowSelection: true,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: true,
    enableColumnActions: false,
    manualSorting: true,
    manualFiltering: true,
    enableColumnOrdering: false,
    manualPagination: true,
    enableTopToolbar: false,
    initialState: {
      showColumnFilters: false,
      density: "compact",
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-select"],
        right: ["mrt-row-actions"],
      },
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: handlePaginationChange,
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedRows,
    rowCount,
    state: {
      rowSelection: selectedRows,
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
      showColumnFilters,
    },
    muiTableBodyCellProps: {
      sx: {
        ".MuiCheckbox-root": {
          padding: 0,
        },
        fontSize: { xs: "2.09vw", sm: "1.2vw", md: "0.695vw" },
        color: "#474747",
        paddingY: { xs: "2vw", sm: "0.7vw" },
        zIndex: 1,
        fontWeight: 400,
        // borderColor: "rgb(246, 210, 127)",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        ".MuiCheckbox-root": {
          padding: 0,
          color: "#FFE097 !important",
        },
        ".css-1lderhz": {
          display: "none !important",
        },
        paddingY: { xs: "2vw", sm: "0.7vw" },
        fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.834vw" },
        background: palette.tab.gradient,
        color: "#ffffff",
        textAlign: "center",
        "& .MuiTableSortLabel-icon": {
          color: "#FFFFFF !important",
        },
      },
    },
    muiFilterTextFieldProps: {
      onKeyDown: (e) => {
        if (e.key === " " && !e.target.value) {
          e.preventDefault();
        }
      },
      onInput: (e) => {
        e.target.value = e.target.value.trimStart();
      },
      sx: {
        "& .MuiSvgIcon-root": {
          fontSize: "small",
        },
        "& .MuiInput-root": {
          "&:hover:not(.Mui-disabled):before": {
            borderBottom: "1px solid black",
          },
          "&.Mui-focused:after": {
            borderBottom: "1px solid white",
          },
        },
        "& input::placeholder": {
          fontSize: { xs: "1.5vw", sm: "1vw", md: "0.7vw" },
        },
      },
      inputProps: {
        sx: {
          fontSize: {
            xs: "2.199vw",
            sm: "clamp(12px, 1.2vw, 16px)",
            md: "0.694vw",
          },
          minHeight: { sm: "0.5em" },
        },
      },
      size: "small",
    },
    muiSelectAllCheckboxProps: {
      size: "small",
      icon: (
        <CheckBoxOutlineBlankRounded
          fontSize="small"
         
        />
      ),
      checkedIcon: (
        <CheckBoxRounded
          fontSize="small"
         
        />
      ),
    },
    muiSelectCheckboxProps: {
      size: "small",
    },
    muiColumnActionsButtonProps: {
      size: "small",
      sx: {
        color: "#FFFFFF",
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
      size: "small",
      color: "primary",
      shape: "rounded",
      variant: "outlined",
      showRowsPerPage: isLargeScreen ? true : false,
    },
    muiTableContainerProps: {
      sx: {
        overflow: "scroll",
        maxWidth: {
          md: toggle ? "82vw" : "93.6vw",
        },
        height: { sm: "80vh", md: "69.5vh" },
        transition: "maxWidth 0.8s ease-in-out",
      },
    },
    muiTableFooterCellProps: {
      align: "right",
      sx: {
        fontSize: { xs: "3.341vw", sm: "1.679vw", md: "0.973vw" },
        ...getGradientTextStyle(palette),
        zIndex: 1,
        fontWeight: 600,
        backgroundColor: "#FFEDD0",
        border: 0,
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0 0 6px 6px",
      },
    },
    localization: {
      clearFilter: t("pages.reports.clear_filter"),
      toggleSelectAll: t("pages.reports.toggle_select_all"),
      toggleSelectRow: t("pages.reports.toggle_select_row"),
      goToFirstPage: t("pages.reports.go_to_first_page"),
      goToNextPage: t("pages.reports.go_to_next_page"),
      goToLastPage: t("pages.reports.go_to_last_page"),
      goToPreviousPage: t("pages.reports.go_to_previous_page"),
      rowsPerPage: t("pages.reports.rows_per_page"),
      sortByColumnAsc: `${t("pages.reports.sort_by_column_asc")}`,
      sortedByColumnAsc: `${t("pages.reports.sorted_by_column_asc")}`,
      sortByColumnDesc: `${t("pages.reports.sort_by_column_desc")}`,
      sortedByColumnDesc: `${t("pages.reports.sorted_by_column_desc")}`,
      filterByColumn: `${t("pages.reports.filter_by_column")}`,
      noRecordsToDisplay: `${t("pages.reports.no_records_to_display")}`,
      noResultsFound: `${t("pages.reports.no_results_found")}`,
    },
  });

  return (
    <Box
      sx={{
        border: `1px solid ${palette.border.gradient}`,
        borderRadius: "8px",
        mt : "0.5%"
      }}
    >
      {formOpenState ? (
        <EmployeeOnboardingStepper
          formOpenState={formOpenState}
          setFormOpenState={setFormOpenState}
          editData={editData}
          setEditData={setEditData}
          setRefresh={handleRefresh}
          onFormClose={handleFormClose}
        />
      ) : viewEmployee ? (
        <ViewEmployee employee={viewData} onBack={handleCloseView} />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1% 1.5%",
              gap: { xs: "1.5vh", sm: "0" },
              background : "#fff6e8",
              borderRadius : "8px 0px"
            }}
          >
            <Box
              sx={{
                width: { xs: "83.59vw", sm: "33%", md: "35%" },
              }}
            >
              <SearchBar
                startAdornment={true}
                placeholder="Search by Name, Code, Status, Location"
                height={{ xs: "10.256vw", sm: "4.317vw", md: "2.778vw" }}
                setSearchKey={setSearchQuery}
                searchKey={searchQuery}
                handleSearch={async () => {
                  await getTableData();
                }}
              />
            </Box>
            <Box
              sx={{
                width: { xs: "100%", sm: "30%" },
                display: "flex",
                alignItems: "center",
                gap: "5%",
                justifyContent: { xs: "center", sm: "right" },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  fontSize: { xs: "2.08vw", sm: "1.68vw", md: "0.80vw" },
                  fontWeight: 600,
                  textWrap: "nowrap",
                }}
                onClick={handleAddEmployee}
                startIcon={<Add fontSize="inherit" sx={{ color: palette.primary.contrastText }} />}
              >
                ADD EMPLOYEE
              </Button>
              {buttonConfigs.map(({ title, onClick, icon }) => (
                <Tooltip
                  key={title}
                  title={t(`pages.reports.${title.toLowerCase()}`)}
                >
                  <IconButton
                    size="small"
                    sx={commonIconButtonStyles}
                    onClick={onClick}
                  >
                    {icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Box>
          <Box>
            <MaterialReactTable table={table} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EmployeeDetails;